// The room's answer to "does this blob have a preview?", kept current as
// clips arrive (lib: ../pool-previews.ts).
//
// Three answers. `true`: draw the waveform from the preview. `false`: there
// is none, draw from the object. `undefined`: a blob the lookup has not
// answered for yet — the view waits rather than guessing, since a guess of
// "object" downloads megabytes the preview would have spared, and a guess of
// "preview" is the 404 this exists to stop. The answer object changes
// identity as answers land, so a callback built on it rebuilds and the
// waveform views ask again.
//
// A lookup that fails (offline, a 5xx) marks its hashes `true`: the preview
// is then requested and a missing one 404s and falls back to the object in
// the player — exactly the behaviour before this hook, never a room that
// draws nothing.

import { useEffect, useMemo, useRef, useState } from "react";

import { lookupPoolPreviews, poolHashesOf } from "../pool-previews";

export interface PoolPreviews {
  /** For a pool hash: whether it has a preview, or undefined until known. */
  hasPreview: (hash: string) => boolean | undefined;
}

const NO_CLIPS: ReadonlyArray<{ audioKey: string | null }> = [];

export function usePoolPreviews(clips: ReadonlyArray<{ audioKey: string | null }> | undefined): PoolPreviews {
  const [known, setKnown] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  // In flight, so a re-render mid-lookup (or StrictMode's second effect run)
  // does not ask twice.
  const asking = useRef(new Set<string>());
  const hashes = useMemo(() => poolHashesOf(clips ?? NO_CLIPS), [clips]);

  useEffect(() => {
    const unknown = hashes.filter((h) => !known.has(h) && !asking.current.has(h));
    if (unknown.length === 0) return;
    for (const h of unknown) asking.current.add(h);
    const learn = (verdict: (hash: string) => boolean) => {
      for (const h of unknown) asking.current.delete(h);
      setKnown((prev) => {
        const next = new Map(prev);
        for (const h of unknown) next.set(h, verdict(h));
        return next;
      });
    };
    lookupPoolPreviews(unknown).then(
      (found) => learn((h) => found.has(h)),
      (err: unknown) => {
        console.warn("[room] preview lookup failed, probing previews instead", err);
        learn(() => true);
      },
    );
  }, [hashes, known]);

  return useMemo(() => ({ hasPreview: (hash: string) => known.get(hash) }), [known]);
}
